import { ColumnsType } from '@setvi/shared/types';
import { useMemo } from 'react';
import { Box } from '@material-ui/core';
import { SText } from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import { Stock } from 'pages/products/components/stock';
import { Product, ProductColumnsIds } from '@setvi/shared/interfaces';
import {
  Profit,
  Cost,
  Price
} from 'pages/products/components/products-list/useProductColumns/contents';
import { ProductLabel, ProductName } from 'componentsV2';
import type { SimilarProductsProps } from '../..';

export const useSimilarProductsColumns = ({
  selectedCustomer,
  openProductDetails,
  configOverride
}: Pick<
  SimilarProductsProps,
  'selectedCustomer' | 'openProductDetails' | 'configOverride'
>) => {
  const visibleIds = useMemo(
    () =>
      configOverride
        ? new Set(configOverride.product?.columns?.map(c => c.id))
        : null,
    [configOverride]
  );

  const columnLabelMap = useMemo(() => {
    if (!configOverride) return null;
    const map = new Map<string, string>();
    configOverride.product?.columns?.forEach(col => {
      if (col.customerView?.columnLabel) {
        map.set(col.id, col.customerView.columnLabel);
      }
    });
    return map;
  }, [configOverride]);

  const getLabel = (id: ProductColumnsIds, fallback: string) =>
    columnLabelMap?.get(id) || fallback;

  const has = (id: ProductColumnsIds) => !visibleIds || visibleIds.has(id);

  const hasPriceColumn =
    has(ProductColumnsIds.Price) ||
    has(ProductColumnsIds.ListPrice) ||
    has(ProductColumnsIds.GetPrice);

  const columns: ColumnsType = useMemo(
    () => [
      {
        id: 'productName',
        label: 'Product Name',
        maxWidth: '50%',
        minWidth: '320px',
        cellContent: (product: Product) => <ProductName product={product} />
      },
      ...(has(ProductColumnsIds.Manufacturer)
        ? [
            {
              id: 'manufacturer',
              maxWidth: '20%',
              minWidth: '180px',
              label: getLabel(
                ProductColumnsIds.Manufacturer,
                'Manufacturer & SKU'
              ),
              cellContent: ({
                vendor,
                manufacturerSku,
                isPrivateLabel,
                vendorRanking
              }: Product) => (
                <Box display="flex" gridGap="4px">
                  <Box display="flex" flexDirection="column" gridGap="4px">
                    <SText lineHeight="24px" title={vendor?.name || '-'} />
                    <SText
                      title={manufacturerSku || '-'}
                      fontColor={TextColor.SlateGrey}
                      lineHeight="20px"
                      size="sm"
                    />
                  </Box>
                  <Box display="flex" gridGap="2px">
                    {isPrivateLabel && <ProductLabel />}
                    {vendorRanking > 0 && (
                      <ProductLabel type="strategic-vendor" />
                    )}
                  </Box>
                </Box>
              )
            }
          ]
        : []),
      ...(has(ProductColumnsIds.Stock)
        ? [
            {
              id: 'stock',
              width: '9%',
              maxWidth: '140px',
              minWidth: '140px',
              label: getLabel(ProductColumnsIds.Stock, 'Stock'),
              cellContent: (product: Product) => {
                const leadTime =
                  // @ts-ignore
                  product?.custom?.ship_lead_time__business_days?.value;
                return (
                  <Box display="flex" flexDirection="column" gridGap="4px">
                    <Stock
                      inventory={product?.inventory}
                      isDropship={product?.dropship}
                      isStockProduct={product?.isStockProduct}
                      selectedCustomer={selectedCustomer}
                      onClick={() => openProductDetails(product)}
                    />
                    {leadTime && (
                      <SText size="sm" lineHeight="20px" title={leadTime} />
                    )}
                  </Box>
                );
              }
            }
          ]
        : []),
      ...(selectedCustomer && hasPriceColumn
        ? [
            {
              id: 'sell',
              width: '7%',
              maxWidth: '100px',
              minWidth: '80px',
              label: getLabel(ProductColumnsIds.Price, 'Sell'),
              cellContent: ({ price }: Product) => <Price value={price} />
            }
          ]
        : []),
      ...(has(ProductColumnsIds.Cost)
        ? [
            {
              id: 'cost',
              width: '7%',
              maxWidth: '100px',
              minWidth: '80px',
              label: getLabel(ProductColumnsIds.Cost, 'Cost'),
              cellContent: ({ cost, customerCost }: Product) => (
                <Cost value={selectedCustomer ? customerCost : cost} />
              )
            }
          ]
        : []),
      ...(selectedCustomer && has(ProductColumnsIds.GP)
        ? [
            {
              id: 'GP',
              width: '7%',
              maxWidth: '100px',
              minWidth: '80px',
              label: getLabel(ProductColumnsIds.GP, 'GP'),
              cellContent: ({ price, cost, customerCost }: Product) => (
                <Profit
                  price={price}
                  cost={selectedCustomer ? customerCost : cost}
                />
              )
            }
          ]
        : [])
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openProductDetails, selectedCustomer, visibleIds, columnLabelMap]
  );

  return { columns };
};
